import { fetchBranches } from './branchApi.js';
import { fetchExpenseSummary } from './expenseApi.js';
import { fetchProducts } from './inventoryApi.js';
import api from './apiconfig.js';

const API_BASE = api.API_BASE;

/**
 * Handle API responses with consistent error handling
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed response data
 * @throws {Error} If response is not ok
 */
const handleResponse = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || 'Server error');
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  return payload;
};

/**
 * Get authorization headers with token
 * @returns {Object} Headers object with Authorization token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Pakistani Rupee currency formatter
const createCurrencyFormatter = (currency = 'PKR') => {
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

export const pkrFormatter = createCurrencyFormatter('PKR');
// ====== DASHBOARD ======

/**
 * Fetch admin dashboard data
 * @returns {Promise<Object>} Dashboard totals, expenses, top products, branch performance, and active tokens
 */
export const fetchAdminDashboardData = async () => {
  try {
    const response = await fetch(`${API_BASE}/admin/dashboard`, {
      headers: getAuthHeaders()
    });
    
    const data = await handleResponse(response);
    
    if (data.success) {
      return {
        totals: data.totals || { today: 0, week: 0, month: 0 },
        totalExpenses: data.totalExpenses || 0,
        topSellingProducts: data.topSellingProducts || [],
        branchPerformance: data.branchPerformance || [],
        activeTokens: data.activeTokens || 0,
        isLoading: false,
        error: null
      };
    }
    
    throw new Error(data.message || 'Failed to fetch dashboard data');
  } catch (error) {
    console.error('Dashboard data fetch error:', error);
    throw error;
  }
};

// ====== REPORTS ======

/**
 * Fetch sales report with optional filters
 * @param {string} dateRange - Date range: 'all', '7d', or '30d' (default)
 * @param {number|null} branchId - Optional branch ID filter
 * @returns {Promise<Object>} Sales data, transactions, and totals
 */
export const fetchSalesReport = async ({ dateRange = '30d', branchId = null, startDate = null, endDate = null } = {}) => {
  try {
    let url = `${API_BASE}/admin/reports/sales?`;

    if (startDate) {
      url += `startDate=${encodeURIComponent(startDate)}&`;
    }

    if (endDate) {
      url += `endDate=${encodeURIComponent(endDate)}&`;
    }

    if (!startDate && !endDate) {
      url += `dateRange=${encodeURIComponent(dateRange)}&`;
    }

    if (branchId) {
      url += `branchId=${encodeURIComponent(branchId)}&`;
    }

    url = url.replace(/[&?]$/, '');

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    const data = await handleResponse(response);
    
    if (data.success) {
      return {
        success: true,
        sales: data.sales || [],
        totals: data.totals || { revenue: 0, count: 0, avgTransaction: 0 }
      };
    }
    
    throw new Error(data.message || 'Failed to fetch sales report');
  } catch (error) {
    console.error('Sales report fetch error:', error);
    throw error;
  }
};

export const fetchProductReports = async ({ dateRange = 'all', branchId = null, startDate = null, endDate = null } = {}) => {
  try {
    let url = `${API_BASE}/admin/reports/products?`;

    if (startDate) {
      url += `startDate=${encodeURIComponent(startDate)}&`;
    }

    if (endDate) {
      url += `endDate=${encodeURIComponent(endDate)}&`;
    }

    if (!startDate && !endDate) {
      url += `dateRange=${encodeURIComponent(dateRange)}&`;
    }

    if (branchId) {
      url += `branchId=${encodeURIComponent(branchId)}&`;
    }

    url = url.replace(/[&?]$/, '');

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });

    const data = await handleResponse(response);
    if (data.success) {
      return {
        products: data.products || []
      };
    }

    throw new Error(data.message || 'Failed to fetch product reports');
  } catch (error) {
    console.error('Product report fetch error:', error);
    throw error;
  }
};

/**
 * Fetch expense report with optional filters
 * @param {string} dateRange - Date range: 'all', '7d', or '30d' (default)
 * @param {number|null} branchId - Optional branch ID filter
 * @returns {Promise<Object>} Expense data, summary, and breakdown by category
 */
export const fetchExpenseReport = async ({ dateRange = '30d', branchId = null, startDate = null, endDate = null } = {}) => {
  try {
    let url = `${API_BASE}/admin/reports/expenses?`;

    if (startDate) {
      url += `startDate=${encodeURIComponent(startDate)}&`;
    }

    if (endDate) {
      url += `endDate=${encodeURIComponent(endDate)}&`;
    }

    if (!startDate && !endDate) {
      url += `dateRange=${encodeURIComponent(dateRange)}&`;
    }

    if (branchId) {
      url += `branchId=${encodeURIComponent(branchId)}&`;
    }

    url = url.replace(/[&?]$/, '');

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    const data = await handleResponse(response);
    
    if (data.success) {
      return {
        success: true,
        expenses: data.expenses || [],
        summary: data.summary || { total: 0, byCategory: {}, count: 0 }
      };
    }
    
    throw new Error(data.message || 'Failed to fetch expense report');
  } catch (error) {
    console.error('Expense report fetch error:', error);
    throw error;
  }
};

/**
 * Fetch inventory report
 * @returns {Promise<Object>} Inventory items and summary (stock levels, low stock count)
 */
export const fetchInventoryReport = async () => {
  try {
    const response = await fetch(`${API_BASE}/admin/reports/inventory`, {
      headers: getAuthHeaders()
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Inventory report fetch error:', error);
    throw error;
  }
};

/**
 * Fetch branch performance metrics
 * @param {string} dateRange - Date range: 'all', '7d', or '30d' (default)
 * @returns {Promise<Object>} Branch metrics (orders, revenue, avg order value, active users)
 */
export const fetchBranchMetrics = async (dateRange = '30d') => {
  try {
    const response = await fetch(`${API_BASE}/admin/metrics/branches?dateRange=${dateRange}`, {
      headers: getAuthHeaders()
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Branch metrics fetch error:', error);
    throw error;
  }
};

/**
 * Fetch sales data (compatibility wrapper for fetchSalesReport)
 * @param {string} dateRange - Date range: 'all', '7d', or '30d' (default)
 * @returns {Promise<Object>} Sales data
 */
export const fetchAdminSales = async (dateRange = '30d') => {
  try {
    const data = await fetchSalesReport(dateRange);
    return { sales: data.sales || [] };
  } catch (error) {
    console.error('Admin sales fetch error:', error);
    throw error;
  }
};

// ====== USER MANAGEMENT ======

/**
 * Fetch all users
 * @returns {Promise<Object>} Array of users with their details
 */
export const fetchUsers = async () => {
  try {
    const response = await fetch(`${API_BASE}/users`, {
      headers: getAuthHeaders()
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Fetch users error:', error);
    throw error;
  }
};

/**
 * Create a new user
 * @param {Object} userData - User data (name, email, role, branchId, password)
 * @returns {Promise<Object>} Created user object
 */
export const createUser = async (userData) => {
  try {
    const response = await fetch(`${API_BASE}/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
};

/**
 * Update user details
 * @param {number} id - User ID
 * @param {Object} updates - Fields to update (name, email, role, branchId, active, password)
 * @returns {Promise<Object>} Updated user object
 */
export const updateUser = async (id, updates) => {
  try {
    const response = await fetch(`${API_BASE}/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Update user error:', error);
    throw error;
  }
};

/**
 * Delete a user
 * @param {number} id - User ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteUser = async (id) => {
  try {
    const response = await fetch(`${API_BASE}/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};

// ====== BACKUP ======

/**
 * Download database backup as SQL file
 * @returns {Promise<Blob>} SQL file blob for download
 */
export const downloadDatabaseBackup = async () => {
  try {
    const response = await fetch(`${API_BASE}/admin/backup/database`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to download backup');
    }

    return await response.blob();
  } catch (error) {
    console.error('Database backup download error:', error);
    throw error;
  }
};

